/* eslint-disable react/no-unescaped-entities */
// @flow
import React from 'react';
import { Strong } from '@finect/tabular-core/Strong';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { UnorderedList } from '@finect/tabular-core/UnorderedList';
import { H2, H3 } from '@finect/tabular-core/Headings';
import { AppLink } from '@finect/tabular-core/AppLink';
import { ArticleDetail, ArticleCTA, LazyPill } from '../../../components';

type Props = {
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Cuesta = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="¿Cuánto cobra un asesor financiero en España?"
      redirectTo={redirectTo}
      toggleModal={toggleModal}
      image="b13ed87fbd3e462d85b4468b5dd835ec"
      schemaORG={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Cuánto cobra un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El coste varía según el modelo de tarifas. Lo más habitual es un porcentaje sobre el capital gestionado, que oscila entre el 0,15% para grandes patrimonios y más del 1% para patrimonios modestos. También existen tarifas fijas o por hora, especialmente para asesorías puntuales, que pueden oscilar entre 200 y 600 euros por hora.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cuáles son las tarifas de un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Las tarifas pueden ser variables (porcentaje del capital), fijas (cuota mensual o anual) o por hora (para consultas puntuales). El precio de un asesor financiero en España suele situarse entre el 0,15% y el 1% anual sobre el patrimonio en el modelo de gestión continua.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cuáles son las comisiones de un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Los asesores pueden cobrar comisiones por los productos que recomiendan, como fondos de inversión, habitualmente entre el 0,4% y el 1% de la inversión. Se trata de un coste "invisible" que no se paga directamente, sino que se descuenta del valor del producto financiero.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Qué cuotas cobrará el asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Algunos asesores optan por cuotas fijas mensuales, una opción que aporta previsibilidad al cliente. Pueden ser tan bajas como 20 euros al mes y, en ciertos modelos, solo se aplican cuando el asesor genera rendimientos positivos para el cliente.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Qué capital mínimo necesito o es recomendable para tener un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Depende del tipo de asesoramiento. Para grandes patrimonios, el mínimo habitual es de 600.000 a 1 millón de euros. Para patrimonios medianos, entre 100.000 y 500.000 euros. Y para patrimonios modestos, algunos asesores ofrecen servicio a partir de 10.000 a 50.000 euros.'
            }
          }
        ]
      }}
    >
      <section>
        <Paragraph>
          Contar con un{' '}
          <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=cuanto_cuesta">
            <Strong>asesor financiero</Strong>
          </AppLink>{' '}
          es, sencillamente, una inversión. Igual que cuando necesitas un dentista, un abogado o un mecánico, un asesor
          es fundamental para sacar partido a tu dinero. Entender <Strong>cuánto cuesta un asesor financiero</Strong> es
          lo principal: puede ser más asequible de lo que imaginas.
        </Paragraph>

        <Paragraph>
          ¿Sabes que la gran mayoría del dinero de los españoles en fondos y planes de pensiones está en productos caros
          y poco rentables? El precio de contratar un asesor financiero, aunque fuera solo para ayudarte a seleccionar
          productos baratos y eficientes, puede compensar con creces el coste de sus servicios.
        </Paragraph>

        <Paragraph>
          Además, ¿sabes que siempre has pagado por el ‘asesoramiento’ cuando acudes a la oficina de tu banco? De hecho,
          desde 2019 tu entidad bancaria debe informarte acerca de cuánto ha ganado contigo durante el año anterior por
          recomendarte determinados productos, gracias a la normativa europea MiFID II. Esta regulación también exige
          que los asesores cuenten con una certificación específica que garantiza que el profesional que te asesora
          cumple los estándares de cualificación mínimos.
        </Paragraph>

        <Paragraph>
          <Strong>¿Cuánto cobra un asesor financiero por consulta o de forma continua?</Strong> A continuación
          desgranamos las formas de cobro más habituales para que puedas comparar y decidir.
        </Paragraph>

        <Paragraph>
          Si te interesa pero no sabes por dónde empezar a buscar,{' '}
          <Strong>
            a través de Finect puedes encontrar{' '}
            <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=cuanto_cuesta">
              asesores financieros y expertos financieros
            </AppLink>{' '}
            de forma completamente gratuita.
          </Strong>{' '}
          Para ello, solo debes rellenar{' '}
          <Strong>
            <AppLink href="#" onClick={(e) => { e.preventDefault(); toggleModal(); }}>
              este formulario
            </AppLink>
          </Strong>{' '}
          y te pondremos en contacto con los asesores que se ajusten a tu perfil y objetivos, para que hagan sus
          propuestas. Luego, podrás establecer relación con el que más encaje con tus necesidades y contratar sus
          servicios para que te ayuden con tus finanzas.
        </Paragraph>

        <Paragraph>La búsqueda de los mejores asesores financieros es totalmente gratuita para ti.</Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>¿Cuánto cobra un asesor financiero? Tipos de tarifa</H2>

        <Paragraph>
          El precio de un asesor financiero o experto financiero depende principalmente del perfil del profesional y del
          modelo de relación que establezca con el cliente.
        </Paragraph>

        <Paragraph>
          Distinguimos dos grandes perfiles y recopilamos las formas de cobro más habituales, teniendo en cuenta que las
          condiciones serán distintas si son profesionales independientes o si trabajan para una entidad:
        </Paragraph>

        <ul>
          <UnorderedList>
            <Strong>Asesores independientes</Strong>
            <Paragraph>
              Estos profesionales no están ligados a ninguna entidad financiera y ejercen de forma autónoma. Al contratar un{' '}
              <Strong>asesor financiero independiente</Strong>, recibirás directamente una factura por sus servicios, que
              puede estructurarse de tres formas:
            </Paragraph>

            <ul>
              <UnorderedList>
                <Strong>Tarifa plana:</Strong> cuota fija mensual o anual, independiente del patrimonio.
              </UnorderedList>
              <UnorderedList>
                <Strong>Por hora:</Strong> el precio de un asesor financiero por consulta puede oscilar entre los{' '}
                <Strong>200 y los 600 euros por hora</Strong>, en función de la experiencia y especialización del
                profesional. Este modelo es especialmente útil para asesorías puntuales.
              </UnorderedList>
              <UnorderedList>
                <Strong>Porcentaje sobre el capital asesorado:</Strong> la opción más habitual en gestión continua.
              </UnorderedList>
            </ul>

            <Paragraph>
              Saber <Strong>cuánto cobra un asesor financiero independiente</Strong> es importante porque, aunque la factura
              es más visible, suele implicar una mayor objetividad en las recomendaciones, al no depender de las comisiones
              de producto.
            </Paragraph>
          </UnorderedList>

          <UnorderedList>
            <Strong>Asesores no independientes</Strong>
            <Paragraph>
              Estos profesionales trabajan vinculados a una entidad financiera y suelen operar con un catálogo de productos
              más restringido. En muchos casos no aplican una tarifa explícita, sino que sus honorarios se traducen en costes
              indirectos: un porcentaje sobre el patrimonio gestionado o comisiones sobre la revalorización de la cartera.
            </Paragraph>
          </UnorderedList>
        </ul>

        <H3>Tarifas sobre capital gestionado</H3>

        <Paragraph>
          El modelo más extendido para entender <Strong>cuánto cobra un asesor financiero</Strong> es el
          porcentaje sobre el capital invertido. Este coste variable puede oscilar entre el{' '}
          <Strong>0,15% para grandes patrimonios</Strong> y <Strong>más del 1% para patrimonios más modestos</Strong>.
        </Paragraph>

        <H3>Comisiones de producto</H3>

        <Paragraph>
          Algunos asesores y gestores financieros cobran comisiones derivadas de los productos que recomiendan, como
          fondos de inversión. Estas comisiones suelen estar entre el <Strong>0,4% y el 1%</Strong> de la inversión y son
          "invisibles": no se pagan de forma directa, sino que se descuentan del valor liquidativo del producto. En una
          inversión de 30.000 euros, esto puede traducirse en aproximadamente 20 euros al mes.
        </Paragraph>

        <Paragraph>
          Si te preguntas <Strong>cuánto cobra un asesor fiscal por consulta</Strong>, los rangos son similares a los
          del asesor financiero generalista: entre 100 y 400 euros por consulta puntual, aunque varía notablemente según
          la complejidad del caso.
        </Paragraph>

        <H3>Cuotas fijas mensuales</H3>

        <Paragraph>
          Algunos asesores financieros optan por cuotas fijas mensuales. Este modelo es atractivo para quienes buscan una
          tarifa predecible: se puede encontrar desde <Strong>20 euros al mes</Strong>, condicionados en ocasiones a que
          el cliente obtenga rendimientos positivos.
        </Paragraph>

        <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

        <H2>¿Cuánto cobra un gestor financiero frente a un asesor?</H2>

        <Paragraph>
          Antes de hablar de precios, conviene aclarar una confusión habitual:{' '}
          <Strong>un asesor financiero y un gestor financiero no son lo mismo</Strong>. El asesor financiero analiza tu
          situación, te propone una estrategia de inversión y te recomienda productos concretos, pero{' '}
          <Strong>la decisión final la tomas tú</Strong>.
        </Paragraph>

        <Paragraph>
          El gestor financiero, en cambio, actúa bajo un mandato de gestión discrecional: tiene autorización para
          comprar y vender activos en tu nombre sin necesidad de consultarte cada operación. Dicho de otro modo, el
          asesor te acompaña y te orienta, mientras que el gestor actúa directamente sobre tu patrimonio. Ambos perfiles
          pueden coexistir pero es importante saber qué estás contratando en cada caso.
        </Paragraph>

        <Paragraph>
          En cuanto al coste, sigue una estructura similar a la del asesor, aunque con alguna particularidad. La tarifa
          más habitual del gestor financiero es un porcentaje anual sobre el patrimonio gestionado, que suele oscilar
          entre el <Strong>0,5% y el 2%</Strong>. A esto pueden sumarse <Strong>comisiones de éxito</Strong>, que se
          aplican sobre la rentabilidad obtenida por encima de un umbral acordado, normalmente entre el 5% y el 10% de
          las ganancias generadas.
        </Paragraph>

        <H2>Capital mínimo necesario para tener un asesor financiero</H2>

        <Paragraph>
          Quizás creas que contratar un asesor financiero está reservado a grandes patrimonios. Sin embargo, en España
          existen opciones adaptadas a todos los niveles de inversión.
        </Paragraph>

        <H3>1. Asesoramiento para grandes patrimonios</H3>

        <Paragraph>
          Para inversores con grandes patrimonios, los asesores suelen ofrecer un servicio personalizado y exclusivo. El
          capital mínimo en este segmento oscila entre los <Strong>600.000 y el millón de euros</Strong>. Estos
          profesionales ofrecen una gestión integral: inversión, planificación financiera, asesoramiento fiscal y otros
          servicios complementarios.
        </Paragraph>

        <H3>2. Asesoramiento para patrimonios medianos</H3>

        <Paragraph>
          Para patrimonios de entre <Strong>100.000 y 500.000 euros</Strong>, muchos asesores financieros ofrecen planes
          de inversión y asesoría con un nivel de personalización notable, pero con condiciones más accesibles.
        </Paragraph>

        <H3>3. Asesoramiento para patrimonios modestos</H3>

        <Paragraph>
          Si dispones de entre <Strong>10.000 y 50.000 euros</Strong>, o incluso menos, también puedes acceder a un
          asesor financiero. En este segmento, los servicios suelen centrarse en asesoría sobre fondos de
          inversión, planificación del ahorro y optimización de productos de bajo coste.
        </Paragraph>

        <H2>Factores que influyen en el capital mínimo necesario</H2>

        <Paragraph>El capital mínimo para contratar un asesor financiero depende de varios elementos:</Paragraph>

        <ul>
          <UnorderedList>
            <Strong>Tipo de asesoramiento:</Strong> el nivel de personalización y la gama de servicios impactan
            directamente en el umbral de entrada.
          </UnorderedList>
          <UnorderedList>
            <Strong>Modelo de negocio:</Strong> los asesores con tarifa fija o por hora son más accesibles para
            patrimonios pequeños, mientras que los que trabajan por porcentaje necesitan un capital mayor para que la
            relación sea rentable para ambas partes.
          </UnorderedList>
          <UnorderedList>
            <Strong>Perfil del asesor:</Strong> un asesor financiero independiente con certificación MiFID II y
            experiencia demostrada suele tener requisitos de entrada más elevados que uno vinculado a una entidad.
          </UnorderedList>
        </ul>

        <Paragraph>
          Muchas veces cuesta pagar por asesoramiento porque tenemos la sensación de que podemos tomar las decisiones
          solos o seguir el consejo del banco. Pero lo mejor que puedes hacer por tu dinero es invertir en la
          orientación de un profesional cualificado.
        </Paragraph>

        <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

        <H2>Preguntas frecuentes</H2>

        <H3>1. ¿Cuánto cobra un asesor financiero?</H3>

        <Paragraph>
          El coste varía según el modelo de tarifas. Lo más habitual es un porcentaje sobre el capital gestionado, que
          oscila entre el <Strong>0,15% para grandes patrimonios</Strong> y{' '}
          <Strong>más del 1% para patrimonios modestos</Strong>. También existen tarifas fijas o por hora, especialmente
          para asesorías puntuales, que pueden oscilar entre <Strong>200 y 600 euros por hora</Strong>.
        </Paragraph>

        <H3>2. ¿Cuáles son las tarifas de un asesor financiero?</H3>

        <Paragraph>
          Las tarifas pueden ser variables (porcentaje del capital), fijas (cuota mensual o anual) o por hora (para
          consultas puntuales). El precio de un asesor financiero en España suele situarse entre el 0,15% y el 1% anual
          sobre el patrimonio en el modelo de gestión continua.
        </Paragraph>

        <H3>3. ¿Cuáles son las comisiones de un asesor financiero?</H3>

        <Paragraph>
          Los asesores pueden cobrar comisiones por los productos que recomiendan, como fondos de inversión,
          habitualmente entre el <Strong>0,4% y el 1%</Strong> de la inversión. Se trata de un coste "invisible" que no
          se paga directamente, sino que se descuenta del valor del producto financiero.
        </Paragraph>

        <H3>4. ¿Qué cuotas cobrará el asesor financiero?</H3>

        <Paragraph>
          Algunos asesores optan por cuotas fijas mensuales, una opción que aporta previsibilidad al cliente. Pueden ser
          tan bajas como <Strong>20 euros al mes</Strong> y, en ciertos modelos, solo se aplican cuando el asesor genera
          rendimientos positivos para el cliente.
        </Paragraph>

        <H3>5. ¿Qué capital mínimo necesito o es recomendable para tener un asesor financiero?</H3>

        <Paragraph>
          Depende del tipo de asesoramiento. Para grandes patrimonios, el mínimo habitual es de 600.000 a 1 millón de
          euros. Para patrimonios medianos, entre 100.000 y 500.000 euros. Y para patrimonios modestos, algunos asesores
          ofrecen servicio a partir de <Strong>10.000 a 50.000 euros.</Strong>
        </Paragraph>

        <hr />

        <H2>Da el primer paso</H2>

        <Paragraph>
          Si te interesa encontrar un asesor financiero pero no sabes por dónde empezar, a través de Finect puedes
          encontrar asesores y expertos financieros de forma completamente gratuita. Solo tienes que rellenar el
          formulario y te pondremos en contacto con los profesionales que mejor se ajusten a tu perfil y objetivos, para
          que presenten sus propuestas. Luego, podrás elegir el que mejor encaje con tus necesidades.
        </Paragraph>

        <Paragraph>
          <Strong>La búsqueda de los mejores asesores financieros es totalmente gratuita para ti.</Strong>
        </Paragraph>

        <LazyPill />
      </section>
    </ArticleDetail>
  );
};
